import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TabSwitch from '../../../shared/components/TabSwitch';
import Preloader from '../../../shared/components/Preloader';
import InviteFriendsModal from '../../social/pages/InviteFriendsModal';
import '../styles/tasksPage.css';
import { achievementsAPI } from '../../../services/api';
import { useDispatch } from 'react-redux';
import { fetchWalletBalance, setBalanceFromServer } from '../../wallet/store/walletSlice';
import { fetchNotifications } from '../../../store/slices/countersSlice';
import { normalizeShopImg } from '../../shop/utils/shopImages';
import { addNewInventoryItems, countInventoryRewards } from '../../inventory/store/inventoryNewSlice';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../i18n/format';
import { moduleTypeLabel } from '../../../shared/utils/gameLabels';

// Иконки наград по типу. CHEST/KEY берут картинку типа из админки
// (chestImageUrl / keyImageUrl в AchievementRewardDTO), остальные — статичные ассеты.
const REWARD_TYPE_ICONS = {
  COINS: '/coin_icon.png',
  TICKETS: '/ticket_icon.png',
  TON_COINS: '/gram.png',
  CHARACTER_MODULE: '/mystery.png',
};

// Случайная часть тела (персонаж выбирается при claim) — «болванка» из рулетки.
// moduleType = null → любая часть.
const MODULE_BLANK_ICONS = {
  HEAD: '/slider/head.png',
  BODY: '/slider/torso.png',
  LEFT_HAND: '/slider/hand_left.png',
  RIGHT_HAND: '/slider/hand_left.png',
  LEFT_LEG: '/slider/leg.png',
  RIGHT_LEG: '/slider/leg_right.png',
};

const rewardIconSrc = (reward) => {
  if (reward.rewardType === 'CHARACTER_MODULE') {
    return MODULE_BLANK_ICONS[reward.moduleType] || REWARD_TYPE_ICONS.CHARACTER_MODULE;
  }
  if (reward.rewardType === 'CHEST') {
    return normalizeShopImg(reward.chestImageUrl) || '/lootbox/icon_chest_green.png';
  }
  if (reward.rewardType === 'KEY') {
    return normalizeShopImg(reward.keyImageUrl) || '/lootbox/icon_key_green.png';
  }
  return REWARD_TYPE_ICONS[reward.rewardType] || null;
};

// Достижение «подписка на канал»: переход в канал + проверка подписки через бэкенд
const SUBSCRIBE_CHANNEL = 'SUBSCRIBE_CHANNEL';
const CHANNEL_URL = 'https://t.me/DATUMEmpire';

const openChannel = () => {
  const webApp = window.Telegram?.WebApp;
  if (webApp?.openTelegramLink) webApp.openTelegramLink(CHANNEL_URL);
  else window.open(CHANNEL_URL, '_blank', 'noopener,noreferrer');
};

// Порядок из админки (achievement_configs.sort_order), внутри группы — по возрастанию порога
const byAdminOrder = (a, b) =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.threshold - b.threshold);

export default function TasksPage() {
  const { t } = useTranslation();
  const claimInFlightRef = useRef(false);
  const [flyingCoins, setFlyingCoins] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState('todo');
  const [claimingId, setClaimingId] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // 'invite_friends' | null
  const [expandedGroups, setExpandedGroups] = useState({});
  const checkInFlightRef = useRef(false);
  const [checkingChannel, setCheckingChannel] = useState(false);
  const [channelHint, setChannelHint] = useState(null); // текст под карточкой подписки
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchAchievements = useCallback(async () => {
    try {
      const data = await achievementsAPI.getAchievements();
      setAchievements(data || []);
    } catch (err) {
      console.error('Failed to fetch achievements:', err);
    } finally {
      // Спиннер показываем только при первой загрузке; последующие refetch — без него
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchAchievements(); }, [fetchAchievements]);

  const handleDeepLink = useCallback((deepLink) => {
    if (!deepLink) return;
    const colonIdx = deepLink.indexOf(':');
    const type = deepLink.slice(0, colonIdx);
    const value = deepLink.slice(colonIdx + 1);

    if (type === 'MODAL') {
      setActiveModal(value);
    } else if (type === 'ROUTE') {
      const [path, search] = value.split('?');
      const state = search ? Object.fromEntries(new URLSearchParams(search)) : undefined;
      navigate(path, { state });
    } else if (type === 'URL') {
      window.open(value, '_blank', 'noopener,noreferrer');
    }
  }, [navigate]);

  const active = achievements.filter(a => a.status === 'ACTIVE');
  const completed = achievements.filter(a => a.status === 'COMPLETED');  // claimable
  const claimed = achievements.filter(a => a.status === 'CLAIMED');       // already received

  // "To Do" — группы по категории (гармошка): порядок групп и элементов — из админки
  const todoGroups = useMemo(() => {
    const sorted = [...active].sort(byAdminOrder);
    const map = new Map();
    sorted.forEach(a => {
      if (!map.has(a.category)) map.set(a.category, []);
      map.get(a.category).push(a);
    });
    return Array.from(map.entries()).map(([category, items]) => ({ category, items }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achievements]);

  // "Completed" — сначала доступные к получению, затем полученные; внутри — порядок админки
  const completedList = useMemo(
    () => [...completed].sort(byAdminOrder).concat([...claimed].sort(byAdminOrder)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [achievements]
  );

  const toggleGroup = (category) =>
    setExpandedGroups(prev => ({ ...prev, [category]: !prev[category] }));

  // Куда и с какой иконкой летит награда при клейме. Цели — плашки строки балансов
  // (Balance.js в AppLayout): coins — слева, GRAM (TON) — по центру, tickets — справа;
  // предметы (часть героя / сундук / ключ) — в иконку пункта меню Inventory.
  const INVENTORY_SELECTOR = '.menu-bar [data-reward-target="inventory"] .icon-wrapper';
  const FLIGHT_BY_TYPE = {
    COINS:     { icon: '/coin_icon.png',   selector: '[data-reward-target="coins"] .icon' },
    TON_COINS: { icon: '/gram.png',        selector: '[data-reward-target="ton"] .icon-ton' },
    TICKETS:   { icon: '/ticket_icon.png', selector: '[data-reward-target="tickets"] .icon' },
    CHARACTER_MODULE: { selector: INVENTORY_SELECTOR },
    CHEST:            { selector: INVENTORY_SELECTOR },
    KEY:              { selector: INVENTORY_SELECTOR },
  };

  const launchRewardFlight = (e, achievement) => {
    const buttonRect = e.currentTarget.getBoundingClientRect();
    // По одной стайке на тип награды; иконка предмета — та же, что в карточке задания
    const flights = [];
    (achievement.rewards || []).forEach(r => {
      const flight = FLIGHT_BY_TYPE[r.rewardType];
      if (!flight || flights.some(f => f.type === r.rewardType)) return;
      flights.push({ type: r.rewardType, selector: flight.selector, icon: flight.icon || rewardIconSrc(r) });
    });
    if (flights.length === 0) flights.push({ type: 'COINS', ...FLIGHT_BY_TYPE.COINS }); // фолбэк — прежнее поведение

    const items = [];
    flights.forEach(({ type, icon, selector }, ti) => {
      const targetEl = document.querySelector(selector);
      if (!targetEl) return;
      const rect = targetEl.getBoundingClientRect();
      for (let i = 0; i < 10; i++) {
        items.push({
          id: `${Date.now()}-${type}-${i}`,
          icon,
          startX: buttonRect.left + buttonRect.width / 2,
          startY: buttonRect.top,
          endX: rect.left + rect.width / 2 - 10,
          endY: rect.top + rect.height / 2 - 10,
          delay: i * 70 + ti * 100,
        });
      }
    });
    if (items.length === 0) return;
    setFlyingCoins(items);
    setTimeout(() => setFlyingCoins([]), 2000);
  };

  const handleClaim = async (e, achievement) => {
    if (claimInFlightRef.current || claimingId || !achievement.userAchievementId) return;
    claimInFlightRef.current = true;
    launchRewardFlight(e, achievement);
    setClaimingId(achievement.userAchievementId);
    try {
      const result = await achievementsAPI.claimAchievement(achievement.userAchievementId);
      // The claim response carries the fresh PlayerBalanceDTO — apply it right away so the
      // header (coins / TON / tickets) updates without waiting for the balance round-trip.
      if (result?.balance) dispatch(setBalanceFromServer(result.balance));
      dispatch(fetchWalletBalance());
      // Части героев / сундуки / ключи → бейдж «новое» на пункте меню Inventory
      dispatch(addNewInventoryItems(countInventoryRewards(achievement.rewards, r => r.moduleCount)));
      // Menu badge (Tasks) = claimable achievements — refresh after every claim
      dispatch(fetchNotifications());
      await fetchAchievements();
    } catch (err) {
      console.error('Claim failed:', err);
    } finally {
      claimInFlightRef.current = false;
      setClaimingId(null);
    }
  };

  const handleCheckSubscription = async () => {
    if (checkInFlightRef.current) return;
    checkInFlightRef.current = true;
    setCheckingChannel(true);
    setChannelHint(null);
    try {
      const { subscribed } = await achievementsAPI.verifyChannelSubscription();
      if (subscribed) {
        // Достижение стало COMPLETED — Claim на вкладке Completed
        dispatch(fetchNotifications());
        await fetchAchievements();
        setTab('completed');
      } else {
        setChannelHint(t('tasks.sub_not_found', 'Subscription not found. Subscribe to the channel and try again.'));
      }
    } catch (err) {
      console.error('Channel subscription check failed:', err);
      setChannelHint(t('tasks.sub_check_failed', 'Could not check subscription. Try again later.'));
    } finally {
      checkInFlightRef.current = false;
      setCheckingChannel(false);
    }
  };

  const rewardLabel = (achievement) => {
    if (!achievement.rewards || achievement.rewards.length === 0) return '';
    return achievement.rewards.map(r => {
      if (r.rewardType === 'COINS') return t('tasks.reward.coins', '+{{n}} coins', { n: formatNumber(r.rewardAmount) });
      if (r.rewardType === 'TICKETS') return t('tasks.reward.tickets', '+{{n}} tickets', { n: r.rewardAmount });
      if (r.rewardType === 'TON_COINS') return `+${r.rewardAmount} GRAM`;
      if (r.rewardType === 'CHARACTER_MODULE') {
        const count = r.moduleCount || 1;
        if (r.moduleType) {
          return t('tasks.reward.part', '+{{n}} × {{part}}', { n: count, part: moduleTypeLabel(t, r.moduleType).toLowerCase() });
        }
        return t('tasks.reward.modules', {
          count,
          defaultValue_one: '+{{count}} module',
          defaultValue_other: '+{{count}} modules',
        });
      }
      if (r.rewardType === 'CHEST') return t('tasks.reward.chest', '+{{n}} chest', { n: r.moduleCount || 1 });
      if (r.rewardType === 'KEY') return t('tasks.reward.key', '+{{n}} key', { n: r.moduleCount || 1 });
      return '';
    }).filter(Boolean).join(' & ');
  };

  const progressLabel = (achievement) => {
    if (achievement.threshold <= 1) return null;
    return `${achievement.progress}/${achievement.threshold}`;
  };

  const progressPC = (achievement) => {
    if (achievement.threshold <= 1) return 100;
    return parseInt(achievement.progress)/parseInt(achievement.threshold)*100;
  };

  const renderCard = (achievement) => (
    <div
      key={achievement.userAchievementId || `${achievement.category}-${achievement.threshold}`}
      className={`task-card-new ${achievement.status === 'COMPLETED' ? 'claimable' : achievement.status === 'CLAIMED' ? 'claimed' : 'todo'}`}
    >
      <div className="task-icon-wrap">
        <img src={achievement.iconUrl ? '/tasks' + achievement.iconUrl : '/referral_icon.svg'} alt="" />
      </div>
      <div className="task-info">
        <span className="task-title-new">{achievement.displayName}</span>
        <span className="task-reward-new">{rewardLabel(achievement)}</span>
      </div>
      {achievement.rewards && achievement.rewards.length > 0 && (
        <div className="task-reward-icons">
          {achievement.rewards.map((r, i) => {
            const src = rewardIconSrc(r);
            return src ? (
              <img key={`${r.rewardType}-${i}`} src={src} alt={r.rewardType} className="task-reward-icon" />
            ) : null;
          })}
        </div>
      )}
      <div className="task-action">
        {achievement.status === 'COMPLETED' && achievement.userAchievementId && (
          <button
            className="app-btn app-btn--green app-btn--sm"
            disabled={claimingId === achievement.userAchievementId}
            onClick={(e) => handleClaim(e, achievement)}
          >
            {claimingId === achievement.userAchievementId ? '...' : t('common.claim', 'Claim')}
          </button>
        )}
        {achievement.status === 'ACTIVE' && achievement.category === SUBSCRIBE_CHANNEL && (
          <div className="task-channel-actions">
            <button className="app-btn app-btn--blue app-btn--sm" onClick={openChannel}>
              {t('tasks.subscribe', 'Subscribe')}
            </button>
            <button
              className="app-btn app-btn--green app-btn--sm"
              disabled={checkingChannel}
              onClick={handleCheckSubscription}
            >
              {checkingChannel ? '...' : t('tasks.check', 'Check')}
            </button>
          </div>
        )}
        {achievement.status === 'ACTIVE' && achievement.category !== SUBSCRIBE_CHANNEL && achievement.deepLink && (
          <img
            src="/main/arrow-v.png"
            alt="→"
            className="task-arrow"
            onClick={() => handleDeepLink(achievement.deepLink)}
          />
        )}
        {achievement.status === 'CLAIMED' && (
          <img src="/stroke.png" alt="✓" className="task-check" />
        )}
      </div>

      {achievement.status === 'ACTIVE' && achievement.category === SUBSCRIBE_CHANNEL && channelHint && (
        <span className="task-channel-hint">{channelHint}</span>
      )}

      {progressLabel(achievement) && achievement.status === 'ACTIVE' && (
        <div className="task-progressbar">
          <div className="task-progressbar__gradient" style={{"width":progressPC(achievement)+'%'}}></div>
          <span className="task-progress">{progressLabel(achievement)}</span>
        </div>
      )}
    </div>
  );

  const tabItems = [
    { key: 'todo', label: t('tasks.tab.todo', 'To Do') },
    { key: 'completed', label: t('tasks.tab.completed', 'Completed'), badge: completed.length },
  ];

  return (
    <div className="tasks-page">
      {flyingCoins.map((coin) => (
        <img
          key={coin.id}
          src={coin.icon || '/coin_icon.png'}
          className="flying-coin"
          alt=""
          style={{
            left: `${coin.startX}px`,
            top: `${coin.startY}px`,
            animationDelay: `${coin.delay}ms`,
            '--x': `${coin.endX - coin.startX}px`,
            '--y': `${coin.endY - coin.startY}px`,
          }}
        />
      ))}

      <TabSwitch tabs={tabItems} activeTab={tab} onTabChange={setTab} />

      <div className="task-list-scroll">
        <div className="task-list">
          {isLoading && <Preloader />}

          {!isLoading && tab === 'todo' && todoGroups.length === 0 && (
            <p className="tasks-empty">{t('tasks.all_done', 'All tasks completed!')}</p>
          )}
          {!isLoading && tab === 'completed' && completedList.length === 0 && (
            <p className="tasks-empty">{t('tasks.none_completed', 'No completed tasks yet.')}</p>
          )}

          {tab === 'todo' && todoGroups.map(({ category, items }) => {
            const collapsible = items.length > 1;
            const expanded = !!expandedGroups[category];
            return (
              <div className="task-group" key={category}>
                <div
                  className={`task-group__items ${collapsible && !expanded ? 'task-group__items--collapsed' : ''}`}
                  style={collapsible ? { maxHeight: expanded ? `${items.length * 130}px` : undefined } : undefined}
                >
                  {items.map(renderCard)}
                </div>
                {collapsible && (
                  <button
                    type="button"
                    className={`task-group-toggle ${expanded ? 'task-group-toggle--expanded' : ''}`}
                    aria-label={expanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}
                    onClick={() => toggleGroup(category)}
                  >
                    <span className="task-group-toggle__chevron" />
                  </button>
                )}
              </div>
            );
          })}

          {tab === 'completed' && completedList.map(renderCard)}
        </div>
      </div>

      {activeModal === 'invite_friends' && (
        <InviteFriendsModal onClose={() => setActiveModal(null)} />
      )}
    </div>
  );
}
